// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useBulk from "@/hooks/useBulk";
import useSelection from "@/hooks/useSelection";
import useShare from "@/hooks/useShare";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyProject } from "@/types";
import { readLangValue } from "@/utils";
import ProjectCard from "@/UI/cards/ProjectCard";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import type { TyBulkField } from "@/UI/components/BulkBar/types";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import ProjectFormPopup from "@/UI/popups/ProjectFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The projects list — the way into everything a company sells.
 *
 * Filters live in the URL, like the drive's, so a filtered list is a link somebody can send. They
 * **replace** rather than push: narrowing a list is an action on what is already on screen, not a
 * place, and pushing would make the back button undo a filter instead of leaving the section.
 */
export default function PropertiesScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const addComp = usePopup((state) => state.addComp);
    const { canShare, share } = useShare();
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const search = params.get("search") ?? "";
    const stages = (params.get("stage") ?? "").split(",").filter(Boolean);
    const types = (params.get("type") ?? "").split(",").filter(Boolean);
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    const [draftStages, setDraftStages] = useState<string[]>(stages);
    const [draftTypes, setDraftTypes] = useState<string[]>(types);

    const isFiltered = !!search || stages.length > 0 || types.length > 0;
    const projects = useFetch<{ projects: TyProject[]; total: number }>("/properties/projects", {}, false);
    const action = useFetch("/properties/projects", {}, false);
    const selection = useSelection();

    const load = () => {
        const query: Record<string, unknown> = { limit: pageSize, page };
        if (search.trim()) query.search = search.trim();
        if (stages.length) query.stage = stages.join(",");
        if (types.length) query.type = types.join(",");
        projects.invoke({ query });
    };

    const bulk = useBulk({ path: "/properties/projects", selection, onDone: load });

    useEffect(() => {
        load();
        // A narrowed or paged list is a different list, so the picks made on the old one go with it.
        selection.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, params.get("stage"), params.get("type"), page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    // A gallery upload finishes in the tray, often from another screen entirely.
    useEffect(() => {
        if (!uploadsFinishedAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadsFinishedAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list puts you back at its start: page 7 of a two-page result is a blank screen.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const openForm = (project?: TyProject) =>
        addComp({
            name: "project-form",
            Component: ProjectFormPopup,
            place: "end",
            anim: "endSlide",
            details: { project: project ?? null, onDone: load },
        });

    const openActions = (project: TyProject, anchor: DOMRect) => {
        const name = readLangValue(project.name, lang) || t.properties.unnamed;
        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openForm(project) },
            /* How a selection starts. Adding to it afterwards is a press on the card itself. */
            { key: "select", label: t.bulk.select, icon: "check", onSelect: () => selection.toggle(project._id) },
            /* Only where there is a page to link to: an unpublished project answers 404. */
            ...(project.isPublic && canShare
                ? [{ key: "share", label: t.common.share, icon: "copy" as const, onSelect: () => share(name, project.slug) }]
                : []),
            {
                key: "remove",
                label: t.common.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.common.remove,
                            message: t.properties.confirmRemoveProject.replace("{name}", name),
                            confirmLabel: t.common.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/properties/projects/${project._id}` }),
                        },
                    }),
            },
        ];

        addComp({
            name: "project-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /**
     * **Three fields, and the API refuses the rest.** A name, a slug, a plan number or a delivery
     * date answers a question about one development — see `ai.plans/bulk-edit-feature.md` §2d. What
     * is left is what a company does in a batch: a phase turning ready, a corrected city, publishing
     * or hiding a set together.
     */
    const bulkFields: TyBulkField[] = [
        {
            key: "stage",
            label: t.properties.fields.stage,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.projectStages.map((item) => ({ value: item, label: t.properties.stages[item] })),
            ],
        },
        { key: "city", label: t.properties.fields.city, kind: "text" },
        {
            key: "isPublic",
            label: t.properties.fields.isPublic,
            kind: "check",
            options: [
                { value: "1", label: t.properties.yes },
                { value: "0", label: t.properties.no },
            ],
        },
    ];

    return (
        <section className="properties-screen">
            <ActionsBar
                addLabel={t.common.add}
                onAdd={() => openForm()}
                isFilterOpen={isFiltering}
                onFilterOpen={() => {
                    setDraftSearch(search);
                    setDraftStages(stages);
                    setDraftTypes(types);
                    setIsFiltering(true);
                }}
                hasFilters={isFiltered}
                onFilterApply={() => {
                    setIsFiltering(false);
                    apply({ search: draftSearch.trim() || null, stage: draftStages.join(",") || null, type: draftTypes.join(",") || null });
                }}
                onFilterClear={() => {
                    setDraftSearch("");
                    setDraftStages([]);
                    setDraftTypes([]);
                    setIsFiltering(false);
                    apply({ search: null, stage: null, type: null });
                }}
                onFilterCancel={() => setIsFiltering(false)}
                filterContent={
                    <>
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.properties.searchProjects}
                        />
                        <MultiSelectInput
                            compact
                            value={draftStages}
                            onChange={setDraftStages}
                            options={constants.projectStages.map((item) => ({ value: item, label: t.properties.stages[item] }))}
                            label={t.properties.fields.stage}
                        />
                        <MultiSelectInput
                            compact
                            value={draftTypes}
                            onChange={setDraftTypes}
                            options={constants.unitTypes.map((item) => ({ value: item, label: t.properties.unitTypes[item] }))}
                            label={t.properties.fields.types}
                        />
                    </>
                }
            />

            {action.error && <p className="properties-screen__state properties-screen__state--error">{action.error}</p>}
            {projects.loading && <p className="properties-screen__state">{t.common.loading}</p>}
            {projects.error && <p className="properties-screen__state properties-screen__state--error">{projects.error}</p>}
            {!projects.loading && !projects.error && !projects.data?.projects.length && (
                <EmptyState
                    icon="properties"
                    title={t.properties.emptyProjects}
                    description={isFiltered ? t.properties.emptyFiltered : undefined}
                    actionLabel={isFiltered ? undefined : t.properties.addProject}
                    onAction={isFiltered ? undefined : () => openForm()}
                />
            )}

            <div className="properties-screen__grid">
                {projects.data?.projects.map((project) => (
                    <ProjectCard
                        key={project._id}
                        project={project}
                        isSelected={selection.has(project._id)}
                        isPicking={selection.isSelecting}
                        // While the bar is up the card picks rather than opens — the gesture every
                        // file manager already taught people, and the bar's cancel is the way out.
                        onOpen={() => (selection.isSelecting ? selection.toggle(project._id) : navigate(`/properties/${project.slug}`))}
                        onActions={(anchor) => openActions(project, anchor)}
                    />
                ))}
            </div>

            {/* One or the other, never both — `hooks/useSelection` says why the pager goes away. */}
            {selection.isSelecting ? (
                <BulkBar
                    count={selection.count}
                    fields={bulkFields}
                    busy={bulk.busy}
                    note={bulk.note}
                    onClear={selection.clear}
                    onApply={(set) => bulk.apply(set)}
                    onDelete={() =>
                        bulk.confirmed({
                            title: t.common.remove,
                            message: t.bulk.confirmRemove.replace("{count}", String(selection.count)),
                            confirmLabel: t.common.remove,
                            doneLabel: t.bulk.removed,
                        })
                    }
                />
            ) : (
                <Paginator
                    page={page}
                    total={projects.data?.total ?? 0}
                    count={projects.data?.projects.length ?? 0}
                    onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
                />
            )}
        </section>
    );
}
